import { z } from 'zod'
import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'
import {
  FloorplanDecorKind,
  FloorplanHistoryEventType,
  FloorplanHistorySource,
  FloorplanFixtureKind,
  FloorplanLengthUnit,
  FloorplanMeasurementSystem,
  FloorplanLightingPreset,
  FloorplanObjectType,
  FloorplanOpeningKind,
  FloorplanRoomShellSourceType,
  FloorplanViewAngle,
  FloorplanSchemaVersion,
} from '../constants/venueLayout'
import { FloorplanKind } from '../constants/venueLayout'
import {
  VenueLayoutTableSize,
  VenueLayoutTableType,
} from '../constants/venueLayoutTable'
import { VenueVisibilityValue } from '../constants/venue'
import {
  createEmptyFloorplanImageDocument,
  floorplanImageDocumentSchema,
} from './floorplanImageDocument'

export const venueLayoutAvailabilitySchema = z.object({
  days: z.array(z.number().int().min(0).max(6)).default([]),
  startDate: z.coerce.date().nullish(),
  endDate: z.coerce.date().nullish(),
  blackoutDates: z.array(z.coerce.date()).nullish(),
})

/** Flat coordinate list [x1,y1,x2,y2,...] */
const polygonFlatSchema = z.array(z.number())

export const floorplanSurfaceMaterialSchema = z.object({
  materialId: z.string().optional().nullable(),
  textureScale: z.number().positive().optional().nullable(),
  tintColor: z.string().optional().nullable(),
  tintOpacity: z.number().min(0).max(1).optional().nullable(),
  darkness: z.number().min(0).max(1).optional().nullable(),
})

const roomShellRoomSchema = z.object({
  id: z.string(),
  name: z.string().optional().nullable(),
  x: z.number().default(0),
  y: z.number().default(0),
  widthCm: z.number().positive().optional().nullable(),
  heightCm: z.number().positive().optional().nullable(),
  polygon: polygonFlatSchema,
  walls: z
    .array(
      z.object({
        id: z.string(),
        kind: z.literal('wall'),
        points: z.tuple([z.number(), z.number(), z.number(), z.number()]),
        thicknessCm: z.number().positive().optional().nullable(),
        heightCm: z.number().nonnegative().optional().nullable(),
        wallColor: z.string().optional().nullable(),
        wallMaterial: floorplanSurfaceMaterialSchema.optional().nullable(),
      })
    )
    .default([]),
  partitions: z
    .array(
      z.object({
        id: z.string(),
        kind: z.literal('partition'),
        points: z.tuple([z.number(), z.number(), z.number(), z.number()]),
        thicknessCm: z.number().positive().optional().nullable(),
        heightCm: z.number().nonnegative().optional().nullable(),
        wallColor: z.string().optional().nullable(),
        wallMaterial: floorplanSurfaceMaterialSchema.optional().nullable(),
      })
    )
    .default([]),
  openings: z
    .array(
      z.object({
        id: z.string(),
        kind: z.nativeEnum(FloorplanOpeningKind),
        wallId: z.string(),
        tAlongWall: z.number().min(0).max(1),
        widthCm: z.number().positive().optional().nullable(),
        rotation: z.number().optional().nullable(),
      })
    )
    .default([]),
  holes: z.array(polygonFlatSchema).optional().nullable(),
  floorColor: z.string().optional().nullable(),
  floorMaterial: floorplanSurfaceMaterialSchema.optional().nullable(),
})

const roomShellWallSchema = z.object({
  id: z.string(),
  points: polygonFlatSchema,
  parentRoomId: z.string().optional().nullable(),
  thicknessCm: z.number().positive().optional().nullable(),
  heightCm: z.number().nonnegative().optional().nullable(),
  wallColor: z.string().optional().nullable(),
  wallMaterial: floorplanSurfaceMaterialSchema.optional().nullable(),
})

const roomShellOpeningSchema = z.object({
  id: z.string(),
  kind: z.nativeEnum(FloorplanOpeningKind),
  wallId: z.string().optional().nullable(),
  parentRoomId: z.string().optional().nullable(),
  x: z.number(),
  y: z.number(),
  widthCm: z.number().positive().optional().nullable(),
  rotation: z.number().optional().nullable(),
})

const roomShellFixtureSchema = z.object({
  id: z.string(),
  kind: z.nativeEnum(FloorplanFixtureKind),
  parentRoomId: z.string().optional().nullable(),
  polygon: polygonFlatSchema.optional().nullable(),
  x: z.number().optional().nullable(),
  y: z.number().optional().nullable(),
  widthCm: z.number().positive().optional().nullable(),
  heightCm: z.number().positive().optional().nullable(),
  rotation: z.number().optional().nullable(),
})

const floorplanObjectParentKindSchema = z.enum(['room'])

const roomShellSourceSchema = z.object({
  type: z.nativeEnum(FloorplanRoomShellSourceType),
  rawFileUrl: z.string().optional().nullable(),
})

const floorplanTableObjectSchema = z.object({
  id: z.string(),
  type: z.literal(FloorplanObjectType.TABLE),
  /** Set when linked to a `VenueLayoutTable`; omit in builder drafts. */
  venueLayoutTableId: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  parentId: z.string().optional().nullable(),
  parentKind: floorplanObjectParentKindSchema.optional().nullable(),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional().nullable(),
  assetId: z.string().optional().nullable(),
  spriteOffsetX: z.number().optional().nullable(),
  spriteOffsetY: z.number().optional().nullable(),
  shadowScaleX: z.number().positive().optional().nullable(),
  shadowScaleY: z.number().positive().optional().nullable(),
  widthCm: z.number().positive().optional().nullable(),
  depthCm: z.number().positive().optional().nullable(),
  heightCm: z.number().nonnegative().optional().nullable(),
  shape: z.nativeEnum(VenueLayoutTableType).optional().nullable(),
  size: z.nativeEnum(VenueLayoutTableSize).optional().nullable(),
})

const floorplanSectionObjectSchema = z.object({
  id: z.string(),
  type: z.literal(FloorplanObjectType.SECTION),
  venueLayoutSectionId: z.string(),
  parentId: z.string().optional().nullable(),
  parentKind: floorplanObjectParentKindSchema.optional().nullable(),
  polygon: polygonFlatSchema.optional().nullable(),
  x: z.number().optional().nullable(),
  y: z.number().optional().nullable(),
  floorColor: z.string().optional().nullable(),
  heightCm: z.number().nonnegative().optional().nullable(),
})

const floorplanLabelObjectSchema = z.object({
  id: z.string(),
  type: z.literal(FloorplanObjectType.LABEL),
  parentId: z.string().optional().nullable(),
  parentKind: floorplanObjectParentKindSchema.optional().nullable(),
  text: z.string(),
  x: z.number(),
  y: z.number(),
  rotation: z.number().optional().nullable(),
})

const floorplanDecorObjectSchema = z.object({
  id: z.string(),
  type: z.literal(FloorplanObjectType.DECOR),
  parentId: z.string().optional().nullable(),
  parentKind: floorplanObjectParentKindSchema.optional().nullable(),
  decorKind: z.nativeEnum(FloorplanDecorKind),
  x: z.number(),
  y: z.number(),
  widthCm: z.number().positive().optional().nullable(),
  heightCm: z.number().nonnegative().optional().nullable(),
  rotation: z.number().optional().nullable(),
})

const floorplanLightingZoneObjectSchema = z.object({
  id: z.string(),
  type: z.literal(FloorplanObjectType.LIGHTING_ZONE),
  parentId: z.string().optional().nullable(),
  parentKind: floorplanObjectParentKindSchema.optional().nullable(),
  polygon: polygonFlatSchema,
  radiusCm: z.number().positive().optional().nullable(),
  preset: z.nativeEnum(FloorplanLightingPreset),
  intensity: z.number().optional().nullable(),
  color: z.string().optional().nullable(),
})

const floorplanThemeSchema = z.object({
  backgroundColor: z.string().optional().nullable(),
  floorTexture: z.string().optional().nullable(),
  lightingPresetId: z.string().optional().nullable(),
  viewAngle: z.nativeEnum(FloorplanViewAngle).optional().nullable(),
})

export const floorplanDocumentObjectSchema = z.discriminatedUnion('type', [
  floorplanTableObjectSchema,
  floorplanSectionObjectSchema,
  floorplanLabelObjectSchema,
  floorplanDecorObjectSchema,
  floorplanLightingZoneObjectSchema,
])

export const roomShellDocumentSchema = z.object({
  unit: z.nativeEnum(FloorplanLengthUnit),
  rooms: z.array(roomShellRoomSchema),
  walls: z.array(roomShellWallSchema).optional().default([]),
  openings: z.array(roomShellOpeningSchema).optional().default([]),
  fixtures: z.array(roomShellFixtureSchema).optional().nullable(),
  source: roomShellSourceSchema.optional().nullable(),
})

/** Locked raster underlay for trace workflows (plan JPG/PNG). Coordinates in the document match this pixel box when set. */
export const floorplanTraceBackgroundSchema = z.object({
  imageUrl: z.string(),
  naturalWidth: z.number().positive(),
  naturalHeight: z.number().positive(),
})

/** Real-world scale: distance between two document-space points equals `distanceCm`. */
export const floorplanCalibrationSchema = z.object({
  pointA: z.tuple([z.number(), z.number()]),
  pointB: z.tuple([z.number(), z.number()]),
  distanceCm: z.number().positive(),
})

export const floorplanDocumentSchema = z.object({
  shell: roomShellDocumentSchema,
  objects: z.array(floorplanDocumentObjectSchema),
  measurementSystem: z
    .nativeEnum(FloorplanMeasurementSystem)
    .default(FloorplanMeasurementSystem.METRIC),
  theme: floorplanThemeSchema.optional().nullable(),
  traceBackground: floorplanTraceBackgroundSchema.optional().nullable(),
  calibration: floorplanCalibrationSchema.optional().nullable(),
})

/** API / Mongoose image subdocument shape (legacy `original` + size variants). */
export const venueLayoutFloorPlanInputSchema = z
  .object({
    original: z.string().optional().nullable(),
    small: z.string().optional().nullable(),
    medium: z.string().optional().nullable(),
    large: z.string().optional().nullable(),
    xlarge: z.string().optional().nullable(),
    filename: z.string().optional().nullable(),
  })
  .refine(
    (value) => {
      const original = value.original?.trim()
      const filename = value.filename?.trim()
      const small = value.small?.trim()
      const medium = value.medium?.trim()
      const large = value.large?.trim()
      const xlarge = value.xlarge?.trim()
      return Boolean(original || filename || small || medium || large || xlarge)
    },
    { message: 'at least one floorPlan image field is required' }
  )

export const venueLayoutCreateArgsSchema = z.object({
  venueId: z.string(),
  name: z.string(),
  availability: venueLayoutAvailabilitySchema.optional().nullable(),
  visibility: z.nativeEnum(VenueVisibilityValue).optional().nullable(),
  /**
   * @deprecated - use floorplanDraft/floorplanPublished instead
   */
  floorPlan: venueLayoutFloorPlanInputSchema,
  /**
   * @deprecated - use visibility instead
   */
  disable: z.boolean().optional(),
})

export const venueLayoutUpdateByIdArgsSchema = z
  .object({
    name: z.string(),
    availability: venueLayoutAvailabilitySchema.partial().nullish(),
    /** V2 image floorplan draft (Konva / legacy image builder). */
    floorplanDraft: floorplanImageDocumentSchema.nullish(),
    floorplanPublished: floorplanImageDocumentSchema.nullish(),
    floorplanV3Draft: floorplanDocumentSchema.nullish(),
    floorplanV3Published: floorplanDocumentSchema.nullish(),
    visibility: z.nativeEnum(VenueVisibilityValue).optional().nullable(),
    historySource: z.nativeEnum(FloorplanHistorySource).optional(),
    //
    /**
     * @deprecated - use floorplanDraft/floorplanPublished instead
     */
    floorPlan: venueLayoutFloorPlanInputSchema,
    /**
     * @deprecated - use visibility instead
     */
    disable: z.boolean().optional(),
  })
  .partial()

const floorplanVersionSnapshotFieldsSchema = dbEntitySchema.extend({
  venueLayout: z.string(),
  venue: z.string(),
  snapshotVersion: z.number().int().min(1),
  floorplanVersion: z.number().int().min(0),
  floorplanKind: z.nativeEnum(FloorplanKind).optional().nullable(),
  eventType: z.nativeEnum(FloorplanHistoryEventType),
  source: z.nativeEnum(FloorplanHistorySource),
  floorplanDocument: floorplanDocumentSchema.optional().nullable(),
  floorplanImageDocument: floorplanImageDocumentSchema.optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
})

export const floorplanVersionSnapshotSchema =
  floorplanVersionSnapshotFieldsSchema.superRefine((value, context) => {
    const hasV3 = value.floorplanDocument != null
    const hasV2 = value.floorplanImageDocument != null
    if (hasV3 === hasV2) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'snapshot requires exactly one of floorplanDocument or floorplanImageDocument',
      })
    }
  })

export const floorplanHistoryEventSchema = dbEntitySchema.extend({
  venueLayout: z.string(),
  venue: z.string(),
  floorplanVersion: z.number().int().min(0),
  eventType: z.nativeEnum(FloorplanHistoryEventType),
  source: z.nativeEnum(FloorplanHistorySource),
  snapshotVersion: z.number().int().min(1).nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
})

export const floorplanHistoryRecordSchema = z.union([
  floorplanHistoryEventSchema.extend({
    kind: z.literal('event'),
  }),
  floorplanVersionSnapshotFieldsSchema.extend({
    kind: z.literal('snapshot'),
  }),
])

export const floorplanVersionListItemSchema =
  floorplanVersionSnapshotFieldsSchema.pick({
    id: true,
    venueLayout: true,
    venue: true,
    snapshotVersion: true,
    floorplanVersion: true,
    eventType: true,
    source: true,
    created: true,
    modified: true,
  })

export const floorplanVersionListResponseSchema = z.object({
  data: z.array(floorplanVersionListItemSchema),
})

export const floorplanVersionResponseSchema = z.object({
  data: floorplanVersionSnapshotSchema,
})

const floorplanLegacySchema = z.object({
  _id: z.string().optional().nullable(),
  id: z.string().optional().nullable(),
  deleted: z.boolean().optional().nullable(),
  primary: z.boolean().optional().nullable(),
  filename: z.string().optional().nullable(),
  small: z.string().optional().nullable(),
  medium: z.string().optional().nullable(),
  large: z.string().optional().nullable(),
  xlarge: z.string().optional().nullable(),
})

export const venueLayoutSchema = dbEntitySchema.extend({
  venue: z.string(),
  name: z.string(),
  order: z.number(),
  availability: venueLayoutAvailabilitySchema.optional().nullable(),
  floorplanVersion: z.number().int().min(0).optional().nullable(),
  floorplanLatestSnapshotVersion: z.number().int().min(0).optional().nullable(),
  floorplanDraft: floorplanImageDocumentSchema.nullish(),
  floorplanPublished: floorplanImageDocumentSchema.nullish(),
  floorplanV3Version: z.number().int().min(0).optional().nullable(),
  floorplanV3LatestSnapshotVersion: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  floorplanV3Draft: floorplanDocumentSchema.nullish(),
  floorplanV3Published: floorplanDocumentSchema.nullish(),
  visibility: z.nativeEnum(VenueVisibilityValue).optional().nullable(),
  /**
   * @deprecated - use floorplanDraft/floorplanPublished instead
   */
  floorPlan: floorplanLegacySchema.nullish(),
  /**
   * @deprecated - use visibility instead
   */
  disable: z.boolean().optional().nullable(),
})

export const floorplanRestoreVersionResponseSchema = z.object({
  layout: venueLayoutSchema,
  restoredVersion: floorplanVersionSnapshotSchema,
})

const floorplanSchemaVersionQueryValueSchema = z.preprocess((value) => {
  if (typeof value === 'string' && value.trim() !== '') {
    const asNumber = Number(value)
    if (!Number.isNaN(asNumber)) return asNumber
  }
  return value
}, z.nativeEnum(FloorplanSchemaVersion))

export const venueLayoutListQuerySchema = z.object({
  ...listQueryZodFields,
  venueId: z.string().or(z.array(z.string())).optional().nullable(),
  activeOnDate: z.coerce.date().optional().nullable(),
  activeOnly: z
    .preprocess((value) => {
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') return true
        if (value.toLowerCase() === 'false') return false
      }
      return value
    }, z.boolean())
    .optional()
    .nullable(),
  floorplanSchemaVersion: z
    .union([
      floorplanSchemaVersionQueryValueSchema,
      z.array(floorplanSchemaVersionQueryValueSchema),
    ])
    .optional()
    .nullable(),
})

export const venueLayoutListSchema = z.object({
  venueId: z.string().or(z.array(z.string())).optional().nullable(),
  activeOnDate: z.coerce.date().optional().nullable(),
  activeOnly: z.boolean().optional().nullable(),
  floorplanSchemaVersion: z
    .union([
      floorplanSchemaVersionQueryValueSchema,
      z.array(floorplanSchemaVersionQueryValueSchema),
    ])
    .optional()
    .nullable(),
  visibilityMin: z.nativeEnum(VenueVisibilityValue).optional().nullable(),
})

export const venueLayoutListResponseSchema = z.object({
  data: z.array(venueLayoutSchema),
})

export {
  buildImageDocFromLayoutEntities,
  createEmptyFloorplanImageDocument,
  floorplanImageDocumentSchema,
  floorplanImageSectionSchema,
  floorplanImageTableSchema,
} from './floorplanImageDocument'

/** Minimal valid V3 document for a new builder session. */
export function createEmptyFloorplanDocument() {
  return floorplanDocumentSchema.parse({
    shell: {
      unit: FloorplanLengthUnit.FT,
      rooms: [],
      walls: [],
      openings: [],
      fixtures: [],
      source: { type: FloorplanRoomShellSourceType.MANUAL },
    },
    objects: [],
    measurementSystem: FloorplanMeasurementSystem.METRIC,
    theme: null,
    traceBackground: null,
    calibration: null,
  })
}
